import { useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { DEPARTMENTS, DEPARTMENT_META, TICKET_STATUSES, TICKET_STATUS_META, formatTicketReference } from '@ssn/shared';
import { useCreateTicket, useTicketList } from '@/lib/queries';
import { useSession } from '@/session/SessionProvider';
import { PageHeader, Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { Select, TextArea, TextInput } from '@/components/ui/Form';
import { InlineError, EmptyState, ErrorState, SkeletonRows } from '@/components/ui/Feedback';
import { Modal } from '@/components/ui/Modal';
import { Pagination } from '@/components/ui/Pagination';
import { TableWrap, Td, Th, Tr } from '@/components/ui/Table';
import { UserChip } from '@/components/ui/Avatar';
import { TicketStatusBadge, DepartmentBadge } from '@/components/domain/ticket-status';
import { formatRelativeTime } from '@/lib/format';

/**
 * The Leadership Request queue (single page, three readings of it):
 * a department lead sees the requests they opened and a button to file a new
 * one; a developer or director sees the wider queue — every open request,
 * narrowing to just their own claims once assigned — exactly matching what
 * the server's visibility predicate already restricts them to.
 */
export function TicketListPage() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const [creating, setCreating] = useState(false);

  const query = useMemo(() => {
    const built: Record<string, unknown> = { page: Number(params.get('page') ?? '1'), pageSize: 25 };
    const department = params.get('department');
    const status = params.get('status');
    if (department) built['department'] = department;
    if (status) built['status'] = status;
    return built;
  }, [params]);

  const { data, isPending, error, refetch } = useTicketList(query);

  const update = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (!value) next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: true });
  };

  const canCreate = can('ticket:create');
  const canSeeDeveloper = can('ticket:read:all') || can('ticket:read:team');

  return (
    <>
      <PageHeader
        title="Leadership Requests"
        description={
          canCreate
            ? 'Requests you have opened on behalf of your department.'
            : 'Requests filed by department leadership. New ones are visible to the whole team until a director assigns them.'
        }
        actions={
          canCreate ? (
            <Button variant="primary" onClick={() => setCreating(true)} icon={<Plus aria-hidden className="size-4" />}>
              New Request
            </Button>
          ) : undefined
        }
      />

      <Panel flush>
        <div className="flex flex-wrap items-end gap-3 border-b border-border p-3">
          <Select
            label="Department"
            value={params.get('department') ?? ''}
            onChange={(event) => update('department', event.target.value)}
            placeholder="Any department"
            options={DEPARTMENTS.map((department) => ({ value: department, label: DEPARTMENT_META[department].label }))}
          />
          <Select
            label="Status"
            value={params.get('status') ?? ''}
            onChange={(event) => update('status', event.target.value)}
            placeholder="Any status"
            options={TICKET_STATUSES.map((status) => ({ value: status, label: TICKET_STATUS_META[status].label }))}
          />
          {(params.get('department') || params.get('status')) && (
            <Button variant="ghost" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
              Clear
            </Button>
          )}
        </div>

        {error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : isPending ? (
          <SkeletonRows rows={5} className="p-4" />
        ) : !data || data.items.length === 0 ? (
          <EmptyState
            title={canCreate ? 'No requests yet' : 'Nothing is waiting'}
            description={
              canCreate
                ? 'File a request and the development team will see it right away.'
                : 'No Leadership Requests match these filters.'
            }
            action={
              canCreate ? (
                <Button size="sm" variant="primary" onClick={() => setCreating(true)}>
                  New Request
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            <TableWrap>
              <thead>
                <tr>
                  <Th className="w-24">Request</Th>
                  <Th>Title</Th>
                  <Th className="w-28">Department</Th>
                  <Th className="w-32">Status</Th>
                  {canSeeDeveloper && <Th className="w-44">Assigned to</Th>}
                  <Th className="w-32" align="right">
                    Updated
                  </Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((ticket) => (
                  <Tr key={ticket.id}>
                    <Td className="font-mono text-xs text-text-subtle">
                      <Link to={`/tickets/${ticket.reference}`} className="hover:text-accent-text hover:underline">
                        {formatTicketReference(ticket.reference)}
                      </Link>
                    </Td>
                    <Td>
                      <Link to={`/tickets/${ticket.reference}`} className="group block min-w-0">
                        <span className="block truncate font-medium text-text group-hover:text-accent-text">
                          {ticket.title}
                        </span>
                        {!canCreate && (
                          <span className="mt-0.5 block truncate text-[11px] text-text-subtle">
                            opened by {ticket.createdBy.displayName}
                          </span>
                        )}
                      </Link>
                    </Td>
                    <Td>
                      <DepartmentBadge department={ticket.department} size="sm" />
                    </Td>
                    <Td>
                      <TicketStatusBadge status={ticket.status} size="sm" />
                    </Td>
                    {canSeeDeveloper && (
                      <Td>
                        <UserChip user={ticket.assignee} size="xs" />
                      </Td>
                    )}
                    <Td align="right" className="text-xs text-text-muted">
                      {formatRelativeTime(ticket.updatedAt)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </TableWrap>

            <Pagination
              page={data.page}
              pageSize={data.pageSize}
              total={data.total}
              totalPages={data.totalPages}
              noun="requests"
              onPageChange={(page) => update('page', String(page))}
            />
          </>
        )}
      </Panel>

      <CreateTicketModal open={creating} onClose={() => setCreating(false)} />
    </>
  );
}

function CreateTicketModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateTicket();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [department, setDepartment] = useState<(typeof DEPARTMENTS)[number]>('APD');

  const close = () => {
    setTitle('');
    setDescription('');
    create.reset();
    onClose();
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate({ title: title.trim(), description: description.trim(), department }, { onSuccess: close });
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="New Leadership Request"
      description="Every developer will see this the moment it is filed."
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button type="submit" form="create-ticket-form" variant="primary" loading={create.isPending}>
            Submit Request
          </Button>
        </>
      }
    >
      <form id="create-ticket-form" onSubmit={submit} className="space-y-4">
        <TextInput
          label="Title"
          required
          autoFocus
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="Add a new dispatch channel"
        />
        <Select
          label="Department"
          required
          value={department}
          onChange={(event) => setDepartment(event.target.value as (typeof DEPARTMENTS)[number])}
          options={DEPARTMENTS.map((dept) => ({ value: dept, label: DEPARTMENT_META[dept].label }))}
        />
        <TextArea
          label="Description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="What do you need, and why?"
          className="min-h-[140px]"
        />
        <InlineError error={create.error} />
      </form>
    </Modal>
  );
}
